import React from "react";
import {Box, CircularProgress, Stack, Typography} from "@mui/material";
import {cropGrowthAlertColor, isCropGrowthAlert} from "../../utils/insightsUtils";
import {StatusIndicator} from "../base/StatusIndicator";
import {useTranslation} from "react-i18next";
import {HelpPopover} from "../base/HelpPopover";

export interface Props {
    cropGrowthPercentage: number | undefined;
    vegetationCover: number | undefined;
    isLoading: boolean;
}

export const FieldCropGrowthCard: React.FC<Props> = ({cropGrowthPercentage, vegetationCover, isLoading}) => {
    const {t} = useTranslation();
    return (
        <Stack direction="column" alignItems="center" gap="1.25rem" position="relative">
            <Stack direction="row" alignItems="center" gap="0.75rem">
                <Typography textTransform="uppercase" fontSize={16} fontWeight={500} color="#545A6E">
                    {t("insights.field_crop_growth.crop_growth")}
                </Typography>
                <StatusIndicator
                    status={
                        cropGrowthPercentage !== undefined && isCropGrowthAlert(cropGrowthPercentage, vegetationCover)
                            ? "cropAlert"
                            : "normal"
                    }
                />
                <Box sx={{position: "absolute", right: "-1.5rem", top: "-1.5rem"}}>
                    <HelpPopover i18nKey="help.popover.fields.crop_growth" placement="top" direction="left" />
                </Box>
            </Stack>
            <Stack direction="column" alignItems="center" gap="0.5rem" position="relative">
                <Typography textTransform="uppercase" fontSize={14} fontWeight={500} color="#737990">
                    {t("insights.field_crop_growth.no_limitation")}
                </Typography>
                <svg width="138" height="160" viewBox="0 0 121 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                        {!isLoading && cropGrowthPercentage !== undefined && !Number.isNaN(cropGrowthPercentage) && (
                            <linearGradient id="cropGrowthGrad" x2="0%" y2="100%">
                                <stop offset={1 - cropGrowthPercentage / 100} stopColor="transparent" />
                                <stop
                                    offset={1 - cropGrowthPercentage / 100}
                                    stopColor={cropGrowthAlertColor(cropGrowthPercentage, vegetationCover)}
                                />
                                #00853B
                            </linearGradient>
                        )}
                    </defs>
                    <path
                        d="M90.9954 1.99032C90.9951 1.98983 91.0036 1.99086 91.0216 1.99484C91.0048 1.99279 90.9957 1.9908 90.9954 1.99032ZM92.1489 2.51578C92.5833 2.77104 93.1096 3.12606 93.7146 3.60415C95.5658 5.06705 98.0527 7.60201 100.782 11.679C106.239 19.83 112.619 34.0758 116.813 57.9831C121.7 85.8383 115.938 107.062 100.018 122.106C79.1482 141.827 43.1347 144.908 19.3906 121.573C-4.3589 98.2337 -1.6438 62.2289 18.6879 42.2479C29.2873 31.8313 38.917 27.7726 48.0875 25.631C52.1503 24.6822 56.1192 24.1096 60.1282 23.5312C60.6968 23.4491 61.2662 23.367 61.8367 23.2836C66.3956 22.6176 71.008 21.8769 75.6327 20.4882C85.1217 17.6388 89.1092 12.7093 90.8388 8.17663C91.6634 6.01562 91.9383 4.03333 92.1244 2.69174L92.1313 2.64174C92.1373 2.59858 92.1432 2.5566 92.1489 2.51578ZM92.3708 1.24971C92.386 1.20138 92.3953 1.18203 92.3961 1.18444C92.3969 1.18685 92.3892 1.21102 92.3708 1.24971Z"
                        stroke="#CACDD8"
                        strokeWidth="4"
                        strokeLinejoin="round"
                        fill="url(#cropGrowthGrad)"
                    />
                    <path
                        d="M0.517578 10.1865C0.517578 10.1865 13.437 9.69725 19.8965 7.2557C26.356 4.81414 30.331 1.88428 30.331 1.88428"
                        stroke="#CACDD8"
                        strokeWidth="4"
                        transform="translate(0,127)"
                    />
                    {!isLoading && (
                        <text x="50%" y="90" textAnchor="middle" fill="#3A3F4F" fontSize={36} fontWeight={700}>
                            {cropGrowthPercentage !== undefined &&
                                !Number.isNaN(cropGrowthPercentage) &&
                                `${Number(cropGrowthPercentage).toFixed(0)}%`}
                        </text>
                    )}
                </svg>
                {isLoading && <CircularProgress sx={{position: "absolute", top: "6rem"}} />}
                <Typography textTransform="uppercase" fontSize={14} fontWeight={500} color="#737990">
                    {t("insights.field_crop_growth.serious_growth_limitation")}
                </Typography>
            </Stack>
        </Stack>
    );
};
